import React, { useEffect, useState } from "react";
import axios from "axios";
import "./CSS/GstReport.css";
import { backend_url } from "../App";
import { exportCsv } from "../utils/exportCsv";

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currencyFmt = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const GstReport = () => {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${backend_url}/gst-report`);
      if (res.data.success) setReport(res.data.report);
      else setError("Failed to load GST report");
    } catch {
      setError("Failed to load GST report");
    } finally {
      setLoading(false);
    }
  };

  const totals = report.reduce(
    (acc, r) => ({
      revenue: acc.revenue + (r.totalRevenue || 0),
      gst: acc.gst + (r.totalGST || 0),
      orders: acc.orders + (r.orderCount || 0),
      b2b: acc.b2b + (r.b2bOrders || 0),
    }),
    { revenue: 0, gst: 0, orders: 0, b2b: 0 }
  );

  const handleExport = () => {
    const header = ["Month", "Year", "Orders", "B2B Orders", "Revenue", "GST Collected"];
    const rows = report.map((r) => [
      MONTH_NAMES[r._id.month],
      r._id.year,
      r.orderCount,
      r.b2bOrders,
      r.totalRevenue,
      r.totalGST,
    ]);
    exportCsv("gst-report.csv", header, rows);
  };

  return (
    <div className="gst-page">
      <div className="gst-banner">
        <div className="gst-title-row">
          <h1 className="gst-title">GST Report</h1>
          <span className="gst-badge">{report.length} months</span>
        </div>
        <p className="gst-subtitle">Monthly revenue and GST collected from paid orders</p>
      </div>

      <div className="gst-stats-row">
        <div className="gst-stat-card">
          <span className="gst-stat-label">Total Revenue</span>
          <span className="gst-stat-value">{currencyFmt(totals.revenue)}</span>
        </div>
        <div className="gst-stat-card">
          <span className="gst-stat-label">Total GST Collected</span>
          <span className="gst-stat-value">{currencyFmt(totals.gst)}</span>
        </div>
        <div className="gst-stat-card">
          <span className="gst-stat-label">Paid Orders</span>
          <span className="gst-stat-value">{totals.orders}</span>
        </div>
        <div className="gst-stat-card">
          <span className="gst-stat-label">B2B (GSTIN) Orders</span>
          <span className="gst-stat-value">{totals.b2b}</span>
        </div>
      </div>

      <div className="gst-content">
        <div className="gst-controls">
          <h3>Monthly Breakdown</h3>
          <button className="gst-export-btn" onClick={handleExport} disabled={!report.length}>
            ⬇ Export CSV
          </button>
        </div>

        <div className="gst-table-wrap">
          {loading ? (
            <div className="gst-empty">Loading report...</div>
          ) : error ? (
            <div className="gst-empty gst-error">{error}</div>
          ) : report.length === 0 ? (
            <div className="gst-empty">No paid orders yet</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Orders</th>
                  <th>B2B Orders</th>
                  <th>Revenue</th>
                  <th>GST Collected</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => (
                  <tr key={`${r._id.year}-${r._id.month}`}>
                    <td className="gst-month-cell">
                      {MONTH_NAMES[r._id.month]} {r._id.year}
                    </td>
                    <td>{r.orderCount}</td>
                    <td>
                      <span className="gst-b2b-chip">{r.b2bOrders}</span>
                    </td>
                    <td>{currencyFmt(r.totalRevenue)}</td>
                    <td className="gst-amount-cell">{currencyFmt(r.totalGST)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default GstReport;
